import { ProductDrawerProps } from './types'
import parse from 'html-react-parser'
import { formatAmount } from 'utils/format'
import styles from './ProductDrawer.module.scss'
import { BlocksRenderer } from '@strapi/blocks-react-renderer'
import { useTranslation } from 'react-i18next'
import { useGetProductsQuery } from 'store/queries/bolt-api/products'
import { useGetLanguage } from 'hooks/use-get-language'
import { Drawer, Heading } from '@boltenergy-be/design-system'
import LoadingSkeleton from 'components/loading-skeleton/LoadingSkeleton.tsx'
import {
  DURATION_CODES,
  EL_PRODUCT_CODES,
  ENERGY_TYPES,
  GAS_PRODUCT_CODES,
  PRODUCT_BILLING_OPTIONS,
  PRODUCT_CONFIG_OPTIONS,
  PRODUCTS_CONFIG
} from '@boltenergy-be/shared'

const ProductDrawer = ({
  product,
  conditions,
  isOpen,
  onRequestClose,
  userType,
  productName,
  needsGas,
  priceListOverride
}: ProductDrawerProps) => {
  // i18n
  const { t } = useTranslation(['contracts', 'common'])
  const lowerCaseLanguage = useGetLanguage('lowercase')

  // Redux queries
  const { data: productsData, isLoading: loadingProductsData } = useGetProductsQuery()

  // constants
  const priceListEl = productsData?.products.electricity?.[EL_PRODUCT_CODES[product]]?.pricelists?.[userType]?.[lowerCaseLanguage]
  const priceListGas = needsGas
    ? productsData?.products.gas?.[GAS_PRODUCT_CODES[product]]?.pricelists?.[userType]?.[lowerCaseLanguage]
    : undefined
  const billingOptions = PRODUCTS_CONFIG[product][PRODUCT_CONFIG_OPTIONS.billingOptions]
    .map((option) => t(($) => $.contact[option === PRODUCT_BILLING_OPTIONS.digital ? 'email' : option], { ns: 'common' }))
    .join(
      ` ${t(($) => $.andOr, {
        ns: 'common'
      })} `
    )
    .toLowerCase()

  return (
    <Drawer
      className={styles.drawer}
      title={product ? (product.toLowerCase().startsWith('plenty') ? productName : `Bolt ${productName}`) : ''}
      {...{ isOpen, onRequestClose }}
    >
      {product ? (
        <>
          <section>
            <ul>
              <li>{parse(t(($) => $.drawer.intro.connections))}</li>
              <li>{parse(t(($) => $.drawer.intro.origins.el))}</li>
              <li>{parse(t(($) => $.drawer.intro.origins.gas))}</li>
              <li>
                {parse(
                  t(($) => $.drawer.intro.type, {
                    type: t(($) => $.tariffType[PRODUCTS_CONFIG[product][PRODUCT_CONFIG_OPTIONS.type]], { ns: 'common' })
                  })
                )}
              </li>
              <li>
                <strong>{t(($) => $.drawer.intro.duration.label)}:</strong>{' '}
                {PRODUCTS_CONFIG[product][PRODUCT_CONFIG_OPTIONS.durationCode] === DURATION_CODES.unlimited
                  ? t(($) => $.drawer.intro.duration.unlimited)
                  : t(($) => $.drawer.intro.duration.yearBased, {
                      count: Number(PRODUCTS_CONFIG[product][PRODUCT_CONFIG_OPTIONS.durationCode]) / 12
                    })}
              </li>
            </ul>
          </section>

          <section>
            <ul>
              {loadingProductsData ? (
                <>
                  <li>
                    <LoadingSkeleton.Rectangle height={20} />
                  </li>
                  {needsGas && (
                    <li>
                      <LoadingSkeleton.Rectangle height={20} />
                    </li>
                  )}
                </>
              ) : (
                <>
                  {!!priceListEl && (
                    <li>
                      <a href={`https://${priceListOverride?.[ENERGY_TYPES.electricity] || priceListEl}`} target="_blank" rel="noreferrer">
                        {t(($) => $.priceList.el, {
                          ns: 'common'
                        })}
                      </a>
                    </li>
                  )}
                  {!!priceListGas && (
                    <li>
                      <a href={`https://${priceListOverride?.[ENERGY_TYPES.gas] || priceListGas}`} target="_blank" rel="noreferrer">
                        {t(($) => $.priceList.gas, {
                          ns: 'common'
                        })}
                      </a>
                    </li>
                  )}
                </>
              )}
            </ul>
          </section>

          <section>
            <Heading as="h2" variant="h6">
              {t(($) => $.drawer.structure.title)}
            </Heading>
            <p>{t(($) => $.drawer.structure.description)}</p>
            <table>
              <thead>
                <tr>
                  <th scope="col">{t(($) => $.drawer.structure.table.payment.head)}</th>
                  <th scope="col">{t(($) => $.drawer.structure.table.receiver.head)}</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td>{t(($) => $.drawer.structure.table.payment.el)}</td>
                  <td>{t(($) => $.drawer.structure.table.receiver.el)}</td>
                </tr>
                <tr>
                  <td>{t(($) => $.drawer.structure.table.payment.gas)}</td>
                  <td>{t(($) => $.drawer.structure.table.receiver.gas)}</td>
                </tr>
                <tr>
                  <td>{t(($) => $.drawer.structure.table.payment.net)}</td>
                  <td>{t(($) => $.drawer.structure.table.receiver.net)}</td>
                </tr>
                <tr>
                  <td>{t(($) => $.drawer.structure.table.payment.platform)}</td>
                  <td>{t(($) => $.drawer.structure.table.receiver.platform)}</td>
                </tr>
                <tr>
                  <td>{t(($) => $.drawer.structure.table.payment.taxes)}</td>
                  <td>{t(($) => $.drawer.structure.table.receiver.taxes)}</td>
                </tr>
              </tbody>
            </table>
          </section>

          <section>
            <Heading as="h2" variant="h6">
              {t(($) => $.drawer.platformCost.title, {
                cost: formatAmount(PRODUCTS_CONFIG[product][PRODUCT_CONFIG_OPTIONS.platformCost][userType])
              })}
            </Heading>
            <p>{t(($) => $.drawer.platformCost.description[product])}</p>
            <table>
              <tbody>
                <tr>
                  <th scope="row">{t(($) => $.drawer.platformCost.table[PRODUCT_CONFIG_OPTIONS.instalmentPeriodicity])}</th>
                  <td>
                    {t(($) => $.periodicity[PRODUCTS_CONFIG[product][PRODUCT_CONFIG_OPTIONS.instalmentPeriodicity]], { ns: 'common' })}
                  </td>
                </tr>
                <tr>
                  <th scope="row">{t(($) => $.drawer.platformCost.table[PRODUCT_CONFIG_OPTIONS.directDebitMandatory])}</th>
                  <td>
                    {PRODUCTS_CONFIG[product][PRODUCT_CONFIG_OPTIONS.directDebitMandatory]
                      ? t(($) => $.directDebit.mandatory, {
                          ns: 'common'
                        })
                      : t(($) => $.directDebit.optional, {
                          ns: 'common'
                        })}
                  </td>
                </tr>
                <tr>
                  <th scope="row">{t(($) => $.drawer.platformCost.table[PRODUCT_CONFIG_OPTIONS.billingOptions])}</th>
                  <td>
                    {t(($) => $.drawer.platformCost.table.billingOptionsValue)} {billingOptions}
                  </td>
                </tr>
                <tr>
                  <th scope="row">{t(($) => $.drawer.platformCost.table[PRODUCT_CONFIG_OPTIONS.contactOptions])}</th>
                  <td>
                    {PRODUCTS_CONFIG[product][PRODUCT_CONFIG_OPTIONS.contactOptions]
                      .map((option, idx) =>
                        idx === 0
                          ? t(($) => $.contact[option], { ns: 'common' })
                          : t(($) => $.contact[option], { ns: 'common' }).toLowerCase()
                      )
                      .join(' & ')}
                  </td>
                </tr>
              </tbody>
            </table>
          </section>

          {conditions && (
            <section className={styles.conditions}>
              <Heading as="h2" variant="h6">
                {t(($) => $.drawer.conditions.title, {
                  productName: productName
                })}
              </Heading>
              <BlocksRenderer content={conditions} />
            </section>
          )}
        </>
      ) : (
        <></>
      )}
    </Drawer>
  )
}

export default ProductDrawer
